import { FUtil, FServiceAPI } from '@freelog/tools-lib';
import type { InformalNodeManagerPageModelState, TreeNode, RuleMatchAndResultParams, RuleMatchAndResultReturn } from './types';

export async function ruleMatchAndResult({
                                           nodeID,
                                           isRematch = false,
                                         }: RuleMatchAndResultParams): Promise<RuleMatchAndResultReturn> {

  if (isRematch) {
    const { errCode, data }: any = await FServiceAPI.InformalNode.rulesRematch({
      nodeId: nodeID,
    });
  }

  while (true) {
    const response: any = await FServiceAPI.InformalNode.testNodeRules({ nodeId: nodeID });
    if (response.data.status === 1) {
      await FUtil.Tool.promiseSleep(200);
    } else {
      return response.data;
    }
  }
}

interface UpdateTreeDataParams {
  list: TreeNode[];
  key: React.Key;
  children: TreeNode[];
}

export function updateTreeData({ list, key, children }: UpdateTreeDataParams): TreeNode[] {
  return list.map(node => {
    if (node.key === key) {
      return {
        ...node,
        children,
      };
    } else if (node.children) {
      return {
        ...node,
        children: updateTreeData({
          list: node.children,
          key,
          children,
        }),
      };
    }
    return node;
  });
}

interface OrganizeData {
  id: string;
  name: string;
  type: string;
  dependencies: OrganizeData[];
}

export function organizeData(data: OrganizeData[], parentKey: string = ''): TreeNode[] {
  return data.map<TreeNode>((d) => {
    const key = parentKey + ':' + (d.type === 'resource' ? '$' : '#') + d.name;

    if (d.dependencies.length === 0) {
      return {
        title: d.name,
        key,
        id: d.id,
        isLeaf: true,
      };
    }

    return {
      title: d.name,
      key,
      id: d.id,
      isLeaf: false,
      children: organizeData(d.dependencies, key),
    };
  });
}

export function simplifiedRelationship(relation: string[]): string[] {
  let arr: string[] = [...relation].sort((a: string, b: string) => a.length - b.length);
  for (let i = 0; i < arr.length; i++) {
    const current: string = arr[i];
    arr = arr.filter((a) => {
      return a === current || !a?.startsWith(current);
    });
  }
  return arr;
}

interface MergeAuthInfoToListParams {
  nodeID: number;
  list: any[];
}

type MergeAuthInfoToListReturn = any[];

export async function mergeAuthInfoToList({ list, nodeID }: MergeAuthInfoToListParams): Promise<MergeAuthInfoToListReturn> {

  if (list.length === 0) {
    return [];
  }

  const params1: Parameters<typeof FServiceAPI.InformalNode.batchGetAuths>[0] = {
    nodeId: nodeID,
    exhibitIds: list.map((d: any) => d.testResourceId).join(','),
    authType: 3,
  };

  const { data }: any = await FServiceAPI.InformalNode.batchGetAuths(params1);

  const exhibitList: InformalNodeManagerPageModelState['exhibit_List'] = list.map((d: any) => {
    return {
      ...d,
      isAuth: data.find((d1: any) => {
        return d1.exhibitId === d.testResourceId;
      }).isAuth,
    };
  });
  return exhibitList;
}

export async function isExhibitsUsed(nodeID: number, names: string[]): Promise<boolean[]> {
  const params: Parameters<typeof FServiceAPI.InformalNode.batchTestResources>[0] = {
    nodeId: nodeID,
    testResourceNames: names.map((n) => {
      return encodeURIComponent(n);
    }).join(','),
  };

  const { data }: any = await FServiceAPI.InformalNode.batchTestResources(params);

  return names.map<boolean>((n) => {
    return data.some((d: any) => {
      return d.testResourceName === n;
    });
  });

}


export async function checkedExhibitUsedNames(nodeID: number, names: string[]): Promise<string[]> {

  const nameMap: Map<string, number> = new Map<string, number>();
  for (const name of names) {
    nameMap.set(name, nameMap.get(name) || 0);
  }

  const parmaNames: string[] = names.map((n) => {
    return (nameMap.get(n) || 0) > 1 ? (n + FUtil.Tool.generateRandomCode()) : n;
  });

  const params: Parameters<typeof FServiceAPI.InformalNode.batchTestResources>[0] = {
    nodeId: nodeID,
    testResourceNames: parmaNames.map((n) => {
      return encodeURIComponent(n);
    }).join(','),
  };

  const { data }: any = await FServiceAPI.InformalNode.batchTestResources(params);

  const newNames: string[] = parmaNames.map<string>((n) => {
    if (data.some((d: any) => {
      return d.testResourceName === n;
    })) {
      return n + FUtil.Tool.generateRandomCode();
    } else {
      return n;
    }
  });
  return newNames;
}
